import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Palette, Star } from 'lucide-react';

/**
 * Brand Kit selector for the selected clip — reuses the EXISTING Brand Kit
 * records (creator + app level) rather than inventing a second branding
 * system. The chosen brandkit_id travels with the clip into the edit spec
 * sent to the rendering backend. `defaultBrandKitId` (the creator's
 * currently-resolved active Brand Kit, same resolution the Main Editor
 * uses) is marked so a clip can be set back to "whatever the creator's
 * default currently is" without hardcoding its id.
 */
export function EditorBrandKitPanel({ clip, brandkits, defaultBrandKitId, onApply, onApplyToAllOfType, nounLabel = 'clips' }) {
  if (!clip) {
    return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="brandkit-panel-empty">Select a clip to apply a Brand Kit.</p>;
  }

  const selected = brandkits.find((b) => b.id === clip.brandkit_id);

  return (
    <div className="space-y-3" data-testid="editor-brandkit-panel">
      <Select value={clip.brandkit_id || 'none'} onValueChange={(v) => onApply(v === 'none' ? null : v)}>
        <SelectTrigger data-testid="brandkit-select">
          <SelectValue placeholder="No Brand Kit" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No Brand Kit</SelectItem>
          {brandkits.map((bk) => (
            <SelectItem key={bk.id} value={bk.id}>
              {bk.name} {bk.type === 'app' ? '(App)' : ''}{bk.id === defaultBrandKitId ? ' · Default' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selected && (
        <div className="p-3 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3" data-testid="brandkit-preview">
          <div className="flex gap-1">
            <span className="w-5 h-5 rounded-full border border-white/20" style={{ background: selected.primary_color }} />
            <span className="w-5 h-5 rounded-full border border-white/20" style={{ background: selected.secondary_color }} />
          </div>
          <div className="text-xs text-[#8A93A6]">
            <p className="text-white flex items-center gap-1">
              {selected.name}
              {selected.id === defaultBrandKitId && <Star className="h-3 w-3 text-amber-400" data-testid="brandkit-default-star" />}
            </p>
            <p>{selected.template_style?.replace('_', ' ')} · {selected.font_family}</p>
          </div>
        </div>
      )}

      {defaultBrandKitId && clip.brandkit_id !== defaultBrandKitId && (
        <Button size="sm" variant="ghost" className="w-full text-xs text-[#8A93A6] hover:text-white" onClick={() => onApply(defaultBrandKitId)} data-testid="brandkit-use-default-button">
          <Star className="h-3.5 w-3.5 mr-1.5" />Use creator default
        </Button>
      )}

      <Button
        size="sm" variant="outline" className="w-full border-white/10"
        onClick={() => onApplyToAllOfType(clip.brandkit_id)}
        data-testid="apply-brandkit-all-button"
      >
        <Palette className="h-4 w-4 mr-2" />
        Apply to all {clip.output_type.toUpperCase()} {nounLabel}
      </Button>
    </div>
  );
}

